import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { z } from 'zod'

import { todayCivilDate } from '~/models/civilDate'
import { addOwnerMemberIssues } from '~/pages/AdminOcctoMaster/tabs/_common/models/ownerMembers'
import { uuidString } from '~/pages/AdminOcctoMaster/tabs/_common/models/uuidString'
import {
  validPeriodRefine,
  validPeriodRefineIssue,
  withinRunes,
} from '~/pages/AdminOcctoMaster/tabs/_common/schema'

/**
 * 発電所合同所有者 input schema。
 */
const PlantMemberInputSchema = z.object({
  businessMasterId: uuidString('事業者を選択してください'),
  isOwn: z.boolean(),
})

export type PlantMemberInput = z.infer<typeof PlantMemberInputSchema>

/**
 * 系統コードの形式チェック: 5 文字の英大文字または数字。エリアは表さない
 * (エリアは areaCode が正)。backend の PlantSystemCode 正規表現: ^[A-Z0-9]{5}$
 */
const plantCodeSchema = z
  .string()
  .min(5, '系統コードは 5文字で入力してください')
  .max(5, '系統コードは 5文字で入力してください')
  .regex(
    /^[A-Z0-9]{5}$/,
    '系統コードは 5文字の英数字 (大文字) で入力してください (例: 31201)',
  )

/**
 * 日付 schema (google.type.Date と同形式)。
 */
const dateSchema = z.object({
  year: z.number().int().min(1900).max(2100),
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(31),
})

/**
 * 発電所マスタの Zod form schema。
 *
 * superRefine で横断バリデーション:
 *  - priority 1-99
 *  - メンバー検証 (isOwn≤1 / 代表包含 / businessMasterId 重複不可) は BgMaster と
 *    共通の addOwnerMemberIssues に委譲
 */
export const PlantMasterFormSchema = z
  .object({
    plantCode: plantCodeSchema,
    plantName: z
      .string()
      .min(1, '発電所名を入力してください')
      .refine(withinRunes(100), '発電所名は100文字以内で入力してください'),
    plantNameAbbr: z
      .string()
      .min(1, '略称を入力してください')
      .refine(withinRunes(50), '略称は50文字以内で入力してください'),
    areaCode: z.nativeEnum(AreaCode, {
      error: () => 'エリアを選択してください',
    }),
    voltageClass: z
      .nativeEnum(VoltageClass, {
        error: () => '電圧階級を選択してください',
      })
      .refine(
        (v) => v !== VoltageClass.UNSPECIFIED,
        '電圧階級を選択してください',
      ),
    /**
     * 最大受電電力 (kW)。必須 (proto required / DB NOT NULL / Go entity の非負検証に一致)。
     * フォーム上は string で受け取り、 非空 + 0 以上の数値にパース可能であることを検証する。
     */
    maxReceivingPowerKw: z
      .string()
      .min(1, '同時最大受電電力を入力してください')
      .refine((v) => {
        const n = Number(v)
        return Number.isFinite(n) && n >= 0
      }, '同時最大受電電力は0以上の数値で入力してください'),
    businessMasterId: uuidString('代表事業者を選択してください'),
    contractIdNo2: z
      .string()
      .refine(withinRunes(20), '契約識別番号2は20文字以内で入力してください')
      .optional(),
    // AC-05/AC-05b/AC-06 (occto-master-audit-fix-bundle.md): 優先順位は任意
    // (未設定 = 空欄で保存可)。 proto optional int32 化に対応。
    priority: z
      .number({ error: () => '優先順位は 1〜99 の整数で入力してください' })
      .int('優先順位は整数で入力してください')
      .min(1, '優先順位は 1 以上で入力してください')
      .max(99, '優先順位は 99 以下で入力してください')
      .optional(),
    // AC-OMP-007: プロラタ内優先順位は項目名を変えた専用の文言を使う (plant.md 付録)
    proRataPriority: z
      .number({
        error: () => 'プロラタ内優先順位は 1〜99 の整数で入力してください',
      })
      .int('プロラタ内優先順位は整数で入力してください')
      .min(1, 'プロラタ内優先順位は 1 以上で入力してください')
      .max(99, 'プロラタ内優先順位は 99 以下で入力してください')
      .optional(),
    // 適用開始日。 proto required / DB NOT NULL / entity validFrom.IsValid() に一致 (必須)。
    validFrom: dateSchema,
    // 適用終了日。 proto / DB とも optional (空欄=継続中)。
    validTo: dateSchema.optional(),
    note: z
      .string()
      .refine(withinRunes(100), '備考は100文字以内で入力してください')
      .optional(),
    members: z
      .array(PlantMemberInputSchema)
      .min(1, '合同所有者を 1件以上指定してください'),
  })
  .superRefine((data, ctx) => {
    // メンバー検証 (isOwn≤1 / 代表包含 / 重複不可) は BgMaster と共通ルール
    addOwnerMemberIssues(ctx, {
      members: data.members,
      repBusinessMasterId: data.businessMasterId,
      memberNoun: '合同所有者',
      repPath: 'businessMasterId',
    })
  })
  // 適用終了日 ≥ 適用開始日 (同日許容、AC-03)。7 タブ共通 refine。
  .refine(validPeriodRefine, validPeriodRefineIssue)

export type PlantMasterFormValues = z.infer<typeof PlantMasterFormSchema>
// Zod v4 では .superRefine で z.input ≠ z.output になる。
// useForm の defaultValues / Control は narrowing 前 (= 部分的に未確定の値を許容する側)
// を受ける必要があるため Input 型を別出し。 submit handler は出力 (= narrow) を受け取る。
export type PlantMasterFormInput = z.input<typeof PlantMasterFormSchema>

/**
 * テスト / storybook 用の valid な初期値生成ヘルパー。
 */
export function makeValidFormValues(
  overrides?: Partial<PlantMasterFormValues>,
): PlantMasterFormValues {
  const businessMasterId = '12345678-1234-4234-8234-123456789012'
  const base: PlantMasterFormValues = {
    plantCode: 'ABCD3',
    plantName: 'テスト発電所',
    plantNameAbbr: 'テスト発',
    areaCode: AreaCode.TOKYO,
    voltageClass: VoltageClass.HIGH,
    maxReceivingPowerKw: '1000',
    businessMasterId,
    contractIdNo2: undefined,
    priority: 1,
    proRataPriority: undefined,
    validFrom: todayCivilDate(),
    validTo: undefined,
    note: undefined,
    members: [{ businessMasterId, isOwn: true }],
  }
  return { ...base, ...overrides } as PlantMasterFormValues
}
